/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {ComponentType} from 'react';
import {DiagnosticBatteryIcon} from '../../../icons/current/DiagnosticBatteryIcon';
import {DiagnosticChargeIcon} from '../../../icons/current/DiagnosticChargeIcon';
import {DiagnosticFuelIcon} from '../../../icons/current/DiagnosticFuelIcon';
import {EngineLightIcon} from '../../../icons/current/EngineLightIcon';
import {GpsQualityIcon} from '../../../icons/current/GpsQualityIcon';
import {SeatbeltIcon} from '../../../icons/current/SeatbeltIcon';
import SystemEVSystemIcon from '../../../icons/current/SystemEVSystemIcon';
import SystemVehicleBatteryIcon from '../../../icons/current/SystemVehicleBattery';
import {dlsColor} from '../../../theme/theme';
import {IconProps, IconSize} from '../../../types/types';
import {getChargeLevelColor} from '../../chargelevel/chargeLevelUtils';

/**
 * The vehicle fuel type driving which Health/Alert indicators an
 * `AssetSummaryCard` displays, per the ION-7392 Vehicle Type Indicator Rules.
 */
export type VehicleFuelType = 'gas' | 'ev' | 'hybrid';

/**
 * The health status of an individual indicator - resolved upstream (e.g. by
 * whatever supplies threshold values per ION-7360) and passed in already
 * decided; indicator components never compute thresholds themselves.
 *
 * `unsupported` (Grey 4, per the Figma "Diagnostic Severity Color Rules"
 * annotation) is only ever meaningful for `seatbelt` - no other indicator
 * has an "unsupported" state.
 */
export type IndicatorStatus = 'default' | 'warning' | 'critical' | 'unsupported';

export type HealthIndicatorKey =
  | 'seatbelt'
  | 'assetBattery'
  | 'fuel'
  | 'charge'
  | 'deviceBattery';

/**
 * Per the "Diagnostic Severity Threshold Color Usage" table: Fuel and Asset
 * Charge still have Warning/Critical thresholds defined elsewhere in the
 * system, but those thresholds do NOT change this card's icon color for
 * those two indicators specifically. Seatbelt (Warning/Critical/Unsupported),
 * Asset Battery (Warning/Critical), and Device Battery (Critical only)
 * actually recolor. `AssetHealthIndicators` enforces this by ignoring any
 * `status` passed for a key not in this list.
 */
export const SEVERITY_AWARE_HEALTH_INDICATORS: HealthIndicatorKey[] = ['seatbelt', 'assetBattery', 'deviceBattery'];

export type AlertIndicatorKey =
  | 'gpsQuality'
  | 'engineLight'
  | 'evSystem';

/**
 * Per the same table, GPS Quality is the only Alert indicator with more
 * than one severity (Warning "Poor" / Critical "No GPS") - Engine Light and
 * EV System are Critical-only, so their icons hardcode Status/Red directly
 * rather than taking a color prop. `AssetAlertIndicators` enforces this by
 * ignoring any `status` passed for a key not in this list.
 */
export const SEVERITY_AWARE_ALERT_INDICATORS: AlertIndicatorKey[] = ['gpsQuality'];

/**
 * The Health indicators applicable to each vehicle type, in display order.
 *
 * `twelveVolt` is intentionally omitted - the Figma samples available at
 * implementation time never rendered a visible 12V indicator for any
 * vehicle type, despite the ticket listing one for all three; needs
 * confirmation with design/ION-7360 before it's added here.
 */
export const HEALTH_INDICATORS_BY_VEHICLE_TYPE: Record<VehicleFuelType, HealthIndicatorKey[]> = {
  gas    : ['seatbelt', 'assetBattery', 'fuel', 'deviceBattery'],
  ev     : ['seatbelt', 'assetBattery', 'charge', 'deviceBattery'],
  hybrid : ['seatbelt', 'assetBattery', 'fuel', 'charge', 'deviceBattery']
};

/**
 * The Alert indicators applicable to each vehicle type, in display order.
 */
export const ALERT_INDICATORS_BY_VEHICLE_TYPE: Record<VehicleFuelType, AlertIndicatorKey[]> = {
  gas    : ['gpsQuality', 'engineLight'],
  ev     : ['gpsQuality', 'evSystem'],
  hybrid : ['gpsQuality', 'evSystem']
};

export const HEALTH_INDICATOR_ICONS: Record<HealthIndicatorKey, ComponentType<IconProps>> = {
  seatbelt      : SeatbeltIcon,
  assetBattery  : SystemVehicleBatteryIcon,
  fuel          : DiagnosticFuelIcon,
  charge        : DiagnosticChargeIcon,
  deviceBattery : DiagnosticBatteryIcon
};

/**
 * Per-indicator icon size for the Diagnostic Summary Bar's Health row -
 * every indicator renders at 24px except Fuel, which Figma specs at 16px.
 */
export const HEALTH_INDICATOR_ICON_SIZES: Record<HealthIndicatorKey, IconSize> = {
  seatbelt      : IconSize.medium,
  assetBattery  : IconSize.medium,
  fuel          : IconSize.small,
  charge        : IconSize.medium,
  deviceBattery : IconSize.medium
};

export const ALERT_INDICATOR_ICONS: Record<AlertIndicatorKey, ComponentType<IconProps>> = {
  gpsQuality  : GpsQualityIcon,
  engineLight : EngineLightIcon,
  evSystem    : SystemEVSystemIcon
};

/**
 * Builds an indicator color resolver from a status-color map factory. The
 * map is rebuilt on every call (not memoized) so `dlsColor` inside it is
 * always resolved against the currently active theme.
 */
const makeIndicatorColorResolver = (
  getColorByStatus: () => Record<IndicatorStatus, string>,
  defaultStatus: IndicatorStatus = 'default'
) => (status: IndicatorStatus = defaultStatus): string => getColorByStatus()[status];

/**
 * Returns the Health icon color for the given status, per the "Diagnostic
 * Severity Color Rules": Grey 3 = Good (or severity not surfaced for that
 * metric - e.g. Seatbelt while Parked), Amber = Warning, Red = Critical,
 * Grey 4 = "Unsupported" (Seatbelt only). "Amber" has no distinct token in
 * the current theme's primary palette, so it maps to the existing `orange`
 * token. Only the icon recolors - the percentage/value text next to it
 * always stays Grey 2, regardless of status.
 */
export const getHealthIndicatorColor = makeIndicatorColorResolver(() => ({
  default     : dlsColor('grey3'),
  warning     : dlsColor('orange'),
  critical    : dlsColor('red'),
  unsupported : dlsColor('grey4')
}));

/**
 * Returns Device Battery's icon color. `warning`/`critical` already come
 * from value-based thresholds further upstream (`getDeviceBatteryStatus`),
 * so those keep `getHealthIndicatorColor`'s existing Amber/Red - but its
 * `default`/`unsupported` cases just fall back to a flat Grey 3/Grey 4,
 * unlike `ChargeLevel` elsewhere in the app, which colors a healthy battery
 * Green rather than grey. This resolves `default`/`unsupported` the same
 * value-based way `ChargeLevel` does (parsing the leading number off
 * `value`, e.g. `"75%"`), falling back to the existing Grey 3/Grey 4 only
 * when `value` has no parseable number.
 */
export const getBatteryIndicatorColor = (status: IndicatorStatus, value: string): string => {
  const percent = parseFloat(value);
  return (status === 'default' || status === 'unsupported') && !Number.isNaN(percent)
    ? getChargeLevelColor(percent)
    : getHealthIndicatorColor(status);
};

/**
 * Seatbelt icon color, per confirmed Figma values: Grey 3 (#767676) for
 * Parked/Buckled/Unsupported, `#E17200` for Error, and Red (`#E2231A`, the
 * `red` token) for Unbuckled. Distinct from `getHealthIndicatorColor` - its
 * Warning and Unsupported colors don't match Seatbelt's (Warning here is a
 * specific hex, not the shared `orange` token; Unsupported here is Grey 3,
 * not Grey 4).
 */
export const getSeatbeltIndicatorColor = makeIndicatorColorResolver(() => ({
  default     : dlsColor('grey3'),
  warning     : dlsColor('orange'),
  critical    : dlsColor('red'),
  unsupported : dlsColor('grey3')
}));

/**
 * Returns the Alert icon color for the given status. Unlike Health, no
 * Alert indicator has an Unsupported state, and only GPS Quality varies
 * between Warning and Critical - Engine Light/EV System are always
 * Critical. GPS Quality's Warning ("Poor") orange is a specific shade
 * confirmed directly against its Figma export (`#E17300`), distinct from
 * the Health indicators' `orange` token, so it's hardcoded here rather than
 * reusing `getHealthIndicatorColor`.
 */
export const getAlertIndicatorColor = makeIndicatorColorResolver(() => ({
  default     : dlsColor('grey3'),
  warning     : dlsColor('orange'),
  critical    : dlsColor('red'),
  unsupported : dlsColor('grey4')
}), 'critical');
